import { useState, useMemo, useEffect } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import { useFinancialData } from '../../context/FinancialDataContext';
import { getFirmSetting, setFirmSetting } from '../../utils/firmSettings';
import { subscribeFirmSettings } from '../../utils/firmSettingsStore';
import { DEFAULT_GROWTH_TARGETS, calculateAnnualizedValue, SERVICE_TIERS, PIPELINE_STAGES, DIVISION_STAGES, LEAD_SOURCES, getEffectiveProbability } from '../../utils/pipelineCalculations';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import SectionLabel from '../common/SectionLabel';
import KPICard from '../common/KPICard';

const TARGETS_KEY = 'bcc_pipeline_targets';
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DIVISION_LABELS = { bwm: 'BWM', bts: 'BTS', bia: 'BIA' };

function sumIncome(monthsData) {
  if (!monthsData) return 0;
  let total = 0;
  for (const mk of MONTH_KEYS) {
    const m = monthsData[mk];
    if (m?.income) {
      total += Object.values(m.income).reduce((s, v) => s + (v || 0), 0);
    }
  }
  return total;
}

function loadTargets() {
  const fromSettings = getFirmSetting('pipelineCoverageTargets', null);
  if (fromSettings) return fromSettings;
  try {
    const stored = localStorage.getItem(TARGETS_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return { ...DEFAULT_GROWTH_TARGETS };
}

export default function PipelineCoverageRatio() {
  const { pipelineKPIs, prospects, sourceStats } = usePipeline();
  const { annualSummary, allPLData, kpi, editableByClass } = useFinancialData();

  const [targets, setTargets] = useState(loadTargets);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    return subscribeFirmSettings((fs) => {
      if (fs.pipelineCoverageTargets) setTargets(fs.pipelineCoverageTargets);
    });
  }, []);

  // 2025 actual revenue (overall)
  const revenue2025 = useMemo(() => {
    const row = annualSummary.find(r => r.year === 2025);
    return row?.revenue || 0;
  }, [annualSummary]);

  // Per-division 2025 revenue from allPLData
  const divisionRevenue2025 = useMemo(() => {
    const result = {};
    for (const cls of ['bwm', 'bts', 'bia']) {
      const data = allPLData?.[cls]?.['2025'];
      result[cls] = data?.months ? sumIncome(data.months) : 0;
    }
    return result;
  }, [allPLData]);

  // Revenue target for 2026 = sum of the per-division targets (each
  // division's 2025 revenue grown by its OWN growth target from the table
  // below). Previously this used a separate standalone targets.overall
  // knob (default 50%), which drifted from the per-division breakdown —
  // the headline read $836,768 @ 50% while the division rollup was
  // $864,115 @ 54.9%. Rolling up from the divisions keeps the headline
  // card, the gap, the coverage bar, and the Company Total row all in
  // agreement, and it updates live as each division's growth input changes.
  const revenueTarget = useMemo(() => {
    return ['bwm', 'bts', 'bia'].reduce(
      (sum, cls) => sum + Math.round((divisionRevenue2025[cls] || 0) * (1 + (targets[cls] || 0))),
      0
    );
  }, [divisionRevenue2025, targets]);

  // Derived overall growth % implied by the rolled-up target (for the
  // headline card subtitle). Replaces the old standalone targets.overall.
  const overallGrowthPct = revenue2025 > 0 ? (revenueTarget - revenue2025) / revenue2025 : 0;

  // Closed-won revenue from pipeline (new deals won through pipeline)
  const closedWonRevenue = useMemo(() => {
    return prospects
      .filter(p => p.stage === 'closed-won')
      .reduce((sum, p) => sum + calculateAnnualizedValue(p.estimatedValue), 0);
  }, [prospects]);

  // Committed revenue = base P&L projected full-year revenue (subscriptions,
  // tax prep, investment mgmt, commissions are all committed/contracted,
  // though not yet collected). Income already includes Closed-Won via the
  // revenue engine, so there is no separate overlay to add.
  const committedRevenue = useMemo(() => {
    const globalData = editableByClass?.global;
    const base = globalData?.months ? sumIncome(globalData.months) : 0;
    return base;
  }, [editableByClass]);

  // Per-division committed revenue from P&L projections.
  const divisionCommitted2026 = useMemo(() => {
    const result = {};
    for (const cls of ['bwm', 'bts', 'bia']) {
      const data = editableByClass?.[cls];
      const base = data?.months ? sumIncome(data.months) : 0;
      result[cls] = base;
    }
    return result;
  }, [editableByClass]);

  // Gap to target (committed is already contracted, pipeline adds to it)
  const gap = Math.max(0, revenueTarget - committedRevenue);

  // Weighted pipeline value (active prospects only)
  const weightedPipeline = pipelineKPIs.weightedValue || 0;

  // Coverage percentage
  const coveragePct = gap > 0 ? weightedPipeline / gap : (weightedPipeline > 0 ? 999 : 0);
  const pipelineCoversGap = weightedPipeline >= gap;

  // Per-division pipeline data
  const divisionPipeline = useMemo(() => {
    const result = {};
    for (const cls of ['bwm', 'bts', 'bia']) {
      const divProspects = prospects.filter(p => {
        const tier = SERVICE_TIERS[p.serviceTier];
        return (p.division === cls) || (tier?.division === cls);
      });
      const won = divProspects
        .filter(p => p.stage === 'closed-won')
        .reduce((sum, p) => sum + calculateAnnualizedValue(p.estimatedValue), 0);
      const active = divProspects
        .filter(p => p.stage !== 'closed-won' && p.stage !== 'closed-lost' && p.stage !== 'unqualified');
      // Use effective probability (manual override or smart-decayed)
      const weighted = active.reduce((sum, p) => {
        const { probability } = getEffectiveProbability(p, sourceStats);
        return sum + (calculateAnnualizedValue(p.estimatedValue) * probability);
      }, 0);
      result[cls] = { won, weighted };
    }
    return result;
  }, [prospects]);

  function handleTargetChange(divKey, value) {
    const numVal = parseFloat(value);
    if (isNaN(numVal)) return;
    setTargets(prev => ({ ...prev, [divKey]: numVal / 100 }));
    setIsDirty(true);
  }

  function handleSave() {
    setFirmSetting('pipelineCoverageTargets', targets);
    localStorage.setItem(TARGETS_KEY, JSON.stringify(targets));
    setIsDirty(false);
  }

  function handleReset() {
    const defaults = { ...DEFAULT_GROWTH_TARGETS };
    setTargets(defaults);
    setFirmSetting('pipelineCoverageTargets', null);
    localStorage.removeItem(TARGETS_KEY);
    setIsDirty(false);
  }

  // Coverage bar widths
  const barTotal = Math.max(weightedPipeline, gap, 1);
  const greenWidth = Math.min((weightedPipeline / barTotal) * 100, 100);
  const grayWidth = gap > weightedPipeline
    ? ((gap - weightedPipeline) / barTotal) * 100
    : 0;

  return (
    <div>
      <SectionLabel>Pipeline Coverage</SectionLabel>

      {/* Top KPI cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <KPICard
          label="Revenue Target"
          value={formatCurrency(revenueTarget)}
          subtitle={`${(overallGrowthPct * 100).toFixed(1)}% growth over 2025 (sum of division targets)`}
        />
        <KPICard
          label="Committed Revenue"
          value={formatCurrency(committedRevenue)}
          subtitle="Contracted subscriptions, tax prep, and investment fees from P&L projection"
        />
        <KPICard
          label="Gap to Target"
          value={formatCurrency(gap)}
          subtitle="New revenue needed beyond committed"
          status={gap > 0 ? 'warn' : 'ok'}
          statusLabel={gap > 0 ? 'Open' : 'Met'}
        />
      </div>

      {/* Coverage bar */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal">
            Weighted Pipeline vs Gap
          </span>
          {pipelineCoversGap ? (
            <span className="text-[12px] font-bold text-deep-teal">
              Pipeline covers {formatPercent(coveragePct)} of gap
            </span>
          ) : (
            <span className="text-[12px] font-bold text-rose">
              Need {formatCurrency(gap - weightedPipeline)} more in pipeline
            </span>
          )}
        </div>
        <div className="w-full h-8 bg-[#e5e5e5] rounded-[4px] overflow-hidden flex">
          <div
            className="h-full bg-deep-teal rounded-l-[4px] transition-all duration-300 flex items-center justify-center"
            style={{ width: `${greenWidth}%` }}
          >
            {greenWidth > 15 && (
              <span className="text-white text-[11px] font-bold">
                {formatCurrency(weightedPipeline)}
              </span>
            )}
          </div>
          {grayWidth > 0 && (
            <div
              className="h-full bg-[#d1d1d1] transition-all duration-300 flex items-center justify-center"
              style={{ width: `${grayWidth}%` }}
            >
              {grayWidth > 15 && (
                <span className="text-[#666] text-[11px] font-bold">
                  {formatCurrency(gap - weightedPipeline)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-[#888]">
          <span>Weighted Pipeline: {formatCurrency(weightedPipeline)}</span>
          <span>Gap: {formatCurrency(gap)}</span>
        </div>
      </div>

      {/* Per-division breakdown table */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">
          Per-Division Breakdown
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[#e0e0e0]">
                <th className="text-left py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Division</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">2025 Revenue</th>
                <th className="text-center py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Growth Target</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">2026 Target</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Won/Committed</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Pipeline (Wtd)</th>
                <th className="text-right py-2 px-2 text-navy font-bold text-[11px] uppercase tracking-wider">Coverage %</th>
              </tr>
            </thead>
            <tbody>
              {['bwm', 'bts', 'bia'].map(cls => {
                const rev2025 = divisionRevenue2025[cls] || 0;
                const growthTarget = targets[cls] || 0;
                const target2026 = Math.round(rev2025 * (1 + growthTarget));
                const divCommitted = divisionCommitted2026[cls] || 0;
                const divWeighted = divisionPipeline[cls]?.weighted || 0;
                const divGap = Math.max(0, target2026 - divCommitted);
                const divCoverage = divGap > 0 ? divWeighted / divGap : (divWeighted > 0 ? 9.99 : 0);

                let coverageColor = 'text-deep-teal';
                if (divCoverage < 0.5) coverageColor = 'text-rose';
                else if (divCoverage < 1.0) coverageColor = 'text-[#c59000]';

                return (
                  <tr key={cls} className="border-b border-[#f0f0f0] hover:bg-bone/40">
                    <td className="py-2 px-2 font-bold text-navy">{DIVISION_LABELS[cls]}</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(rev2025)}</td>
                    <td className="py-2 px-2 text-center">
                      <div className="inline-flex items-center gap-1">
                        <input
                          type="number"
                          className="w-16 text-center border border-[#d0d0d0] rounded px-1 py-0.5 text-[13px] focus:outline-none focus:border-bullogic-blue"
                          value={Math.round(growthTarget * 100)}
                          onChange={e => handleTargetChange(cls, e.target.value)}
                          min={0}
                          max={500}
                          step={5}
                        />
                        <span className="text-[#888]">%</span>
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right">{formatCurrency(target2026)}</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(divCommitted)}</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(divWeighted)}</td>
                    <td className={`py-2 px-2 text-right font-bold ${coverageColor}`}>
                      {formatPercent(divCoverage)}
                    </td>
                  </tr>
                );
              })}
              {/* Company total row */}
              {(() => {
                const totalRev2025 = revenue2025;
                // Reuse the headline revenueTarget so the Company Total row
                // and the top card can never drift apart.
                const totalTarget2026 = revenueTarget;
                const totalCommitted = committedRevenue;
                const totalWeighted = pipelineKPIs.weightedValue || 0;
                const totalGap = Math.max(0, totalTarget2026 - totalCommitted);
                const totalCoverage = totalGap > 0 ? totalWeighted / totalGap : (totalWeighted > 0 ? 9.99 : 0);
                const totalGrowthPct = overallGrowthPct;

                let coverageColor = 'text-deep-teal';
                if (totalCoverage < 0.5) coverageColor = 'text-rose';
                else if (totalCoverage < 1.0) coverageColor = 'text-[#c59000]';

                return (
                  <tr className="border-t-2 border-navy/20 bg-bone/50">
                    <td className="py-2.5 px-2 font-bold text-navy">Company Total</td>
                    <td className="py-2.5 px-2 text-right font-bold">{formatCurrency(totalRev2025)}</td>
                    <td className="py-2.5 px-2 text-center text-[12px] font-bold text-navy">
                      {(totalGrowthPct * 100).toFixed(1)}%
                    </td>
                    <td className="py-2.5 px-2 text-right font-bold">{formatCurrency(totalTarget2026)}</td>
                    <td className="py-2.5 px-2 text-right font-bold">{formatCurrency(totalCommitted)}</td>
                    <td className="py-2.5 px-2 text-right font-bold">{formatCurrency(totalWeighted)}</td>
                    <td className={`py-2.5 px-2 text-right font-bold ${coverageColor}`}>
                      {formatPercent(totalCoverage)}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Save / Reset buttons */}
        <div className="flex gap-3 mt-4 justify-end">
          <button
            onClick={handleReset}
            className="px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider border border-[#d0d0d0] rounded-[4px] text-[#666] hover:bg-[#f5f5f5] transition-colors"
          >
            Reset Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty}
            className={`px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider rounded-[4px] transition-colors ${
              isDirty
                ? 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
                : 'bg-[#e0e0e0] text-[#aaa] cursor-not-allowed'
            }`}
          >
            Save Targets
          </button>
        </div>
      </div>

      {/* Prospect Requirements - working backwards from the gap */}
      <ProspectRequirements
        prospects={prospects}
        divisionRevenue2025={divisionRevenue2025}
        divisionCommitted2026={divisionCommitted2026}
        divisionPipeline={divisionPipeline}
        targets={targets}
      />
    </div>
  );
}

// Separate component for prospect requirements to keep the main component clean
function ProspectRequirements({ prospects, divisionRevenue2025, divisionCommitted2026, divisionPipeline, targets }) {
  const requirements = useMemo(() => {
    return ['bwm', 'bts', 'bia'].map(cls => {
      const rev2025 = divisionRevenue2025[cls] || 0;
      const target2026 = Math.round(rev2025 * (1 + (targets[cls] || 0)));
      const committed = divisionCommitted2026[cls] || 0;
      const weighted = divisionPipeline[cls]?.weighted || 0;
      const gap = Math.max(0, target2026 - committed - weighted);

      // Average deal size for this division (from won + active prospects)
      const divProspects = (prospects || []).filter(p => {
        const div = p.division || SERVICE_TIERS[p.serviceTier]?.division;
        return div === cls;
      });

      // Average deal value: use actual prospect data, fall back to service tier defaults
      const dealsWithValue = divProspects.filter(p =>
        calculateAnnualizedValue(p.estimatedValue) > 0
      );
      let avgDealValue;
      let avgDealSampleSize = dealsWithValue.length;
      let avgDealValueSource;

      if (dealsWithValue.length > 0) {
        avgDealValue = dealsWithValue.reduce((s, p) => s + calculateAnnualizedValue(p.estimatedValue), 0) / dealsWithValue.length;
        avgDealValueSource = dealsWithValue.length >= 3 ? 'observed' : 'limited';
      } else {
        // Fall back to service tier defaults for this division
        const tierDefaults = Object.values(SERVICE_TIERS).filter(t => t.division === cls);
        if (tierDefaults.length > 0) {
          avgDealValue = tierDefaults.reduce((s, t) => {
            return s + (t.defaultOneTime || 0) + ((t.defaultMonthly || 0) * 12);
          }, 0) / tierDefaults.length;
          avgDealValueSource = 'default';
          avgDealSampleSize = 0;
        } else {
          avgDealValue = 0;
          avgDealValueSource = 'default';
          avgDealSampleSize = 0;
        }
      }

      // Discovery-to-close conversion rate
      const divStages = DIVISION_STAGES[cls] || DIVISION_STAGES.bwm;
      const firstStage = divStages[0];
      const enteredFirst = divProspects.filter(p =>
        p.stageHistory && p.stageHistory.some(h => h.stageId === firstStage)
      );
      const closedWon = enteredFirst.filter(p => p.stage === 'closed-won');

      // Need at least 3 outcomes (won + lost) with at least 1 won to trust observed rate
      const totalOutcomes = divProspects.filter(p =>
        p.stage === 'closed-won' || p.stage === 'closed-lost' || p.stage === 'unqualified'
      ).length;
      const hasReliableData = totalOutcomes >= 3 && closedWon.length > 0;

      let effectiveConversion;
      let conversionSource;
      if (hasReliableData) {
        effectiveConversion = closedWon.length / totalOutcomes;
        conversionSource = 'observed';
      } else {
        // Estimated: use conservative defaults based on division complexity
        // BWM (5-stage): ~25% overall, BTS (3-stage): ~40%, BIA (1-stage): ~60%
        const estimatedRates = { bwm: 0.25, bts: 0.40, bia: 0.60 };
        effectiveConversion = estimatedRates[cls] || 0.25;
        conversionSource = 'estimated';
      }

      // Work backwards: gap -> deals needed -> prospects needed
      const dealsNeeded = avgDealValue > 0 ? Math.ceil(gap / avgDealValue) : 0;
      const prospectsNeeded = dealsNeeded != null && effectiveConversion > 0
        ? Math.ceil(dealsNeeded / effectiveConversion)
        : null;

      return {
        division: cls,
        label: DIVISION_LABELS[cls],
        gap,
        avgDealValue,
        avgDealValueSource,
        avgDealSampleSize,
        effectiveConversion,
        conversionSource,
        dealsNeeded,
        prospectsNeeded,
        firstStageLabel: PIPELINE_STAGES.find(s => s.id === firstStage)?.label || firstStage,
      };
    }).filter(r => r.gap > 0); // Only show divisions that still have a gap
  }, [prospects, divisionRevenue2025, divisionCommitted2026, divisionPipeline, targets]);

  if (requirements.length === 0) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mt-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">
          Prospect Requirements
        </div>
        <p className="text-[13px] text-deep-teal font-bold text-center py-4">
          All division targets are covered by committed revenue + pipeline. No additional prospects needed.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mt-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-1">
        Prospect Requirements
      </div>
      <p className="text-[11px] font-light text-[#888] mb-4">
        Working backwards from the revenue gap using average deal size and conversion rates per division.
      </p>

      <div className="grid grid-cols-3 gap-4">
        {requirements.map(req => (
          <div
            key={req.division}
            className="border border-gray-200 rounded-[4px] p-4"
          >
            <div className="text-[13px] font-bold text-navy mb-3">{req.label}</div>

            {/* The headline number */}
            <div className="text-center mb-4">
              <div className="text-[36px] font-bold text-navy leading-tight">
                {req.prospectsNeeded != null ? req.prospectsNeeded : '?'}
              </div>
              <div className="text-[11px] text-[#888]">
                prospects needed in {req.firstStageLabel}
              </div>
            </div>

            {/* The math breakdown */}
            <div className="space-y-2 text-[12px] border-t border-gray-100 pt-3">
              <div className="flex justify-between">
                <span className="text-[#888]">Revenue Gap</span>
                <span className="font-bold text-rose">{formatCurrency(req.gap)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#888]">Avg Deal Value</span>
                <div className="text-right">
                  <span className="font-bold text-navy">
                    {req.avgDealValue != null ? formatCurrency(req.avgDealValue) : 'N/A'}
                  </span>
                  <span className={`text-[9px] ml-1 px-1 py-0.5 rounded-[2px] ${
                    req.avgDealValueSource === 'observed' ? 'bg-deep-teal/10 text-deep-teal' : 'bg-gray-100 text-[#aaa]'
                  }`}>
                    n={req.avgDealSampleSize}
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-[#888]">Deals to Close</span>
                <span className="font-bold text-navy">{req.dealsNeeded != null ? req.dealsNeeded : '?'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#888]">Conversion Rate</span>
                <div className="text-right">
                  <span className="font-bold text-navy">
                    {formatPercent(req.effectiveConversion)}
                  </span>
                  <span className={`text-[9px] ml-1 px-1 py-0.5 rounded-[2px] ${
                    req.conversionSource === 'observed' ? 'bg-deep-teal/10 text-deep-teal' : 'bg-gray-100 text-[#aaa]'
                  }`}>
                    {req.conversionSource}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-[#aaa] mt-3">
        Conversion rates marked "observed" are calculated from your actual pipeline data. "Estimated" rates use stage probability defaults until enough deals close (3+ outcomes needed).
      </p>
    </div>
  );
}
